/**
 * Bundle Offer — free gift with a trigger product
 *
 * Offers are configured per product template on the product information section
 * ("Free gift offer" settings).  Each configured PDP renders a
 * `[data-bundle-offer]` element (see snippets/free-gift-offer.liquid) that this
 * module reads.  When the trigger product is added to the cart the gift variant
 * is added too; if the trigger product later leaves the cart the gift is removed.
 *
 * Every gift line carries a hidden `_gift_trigger` property holding the trigger
 * product id, so cart-page cleanup works without any offer config on the page.
 */

import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/* -------------------------------------------------------------------------- */
/*  Constants                                                                 */
/* -------------------------------------------------------------------------- */

/** Hidden line item property linking a gift line back to its trigger product. */
const TRIGGER_PROPERTY_KEY = '_gift_trigger';

/**
 * @typedef {object} BundleOffer
 * @property {string} triggerProductId
 * @property {string} giftVariantId
 * @property {boolean} requiresSubscription
 * @property {string} propertyKey
 * @property {string} propertyValue
 */

/* -------------------------------------------------------------------------- */
/*  Config                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Reads every offer payload rendered on the current page.
 * @returns {BundleOffer[]}
 */
function readOffers() {
	return Array.from(document.querySelectorAll('[data-bundle-offer]'))
		.map((element) => {
			const data = /** @type {HTMLElement} */ (element).dataset;
			if (!data.triggerProductId || !data.giftVariantId) return null;

			return {
				triggerProductId: data.triggerProductId,
				giftVariantId: data.giftVariantId,
				requiresSubscription: data.requiresSubscription !== 'false',
				propertyKey: data.propertyKey || 'Free gift',
				propertyValue: data.propertyValue || '',
			};
		})
		.filter(/** @returns {offer is BundleOffer} */ (offer) => offer !== null);
}

/* -------------------------------------------------------------------------- */
/*  BundleOfferManager                                                        */
/* -------------------------------------------------------------------------- */

class BundleOfferManager {
	/** @type {BundleOffer[]} */
	#offers = [];

	/** @type {boolean} */
	#adding = false;

	/** @type {boolean} */
	#syncing = false;

	/** @type {((...args: any[]) => Promise<Response>) | null} */
	#originalFetch = null;

	constructor() {
		this.#offers = readOffers();

		document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
		this.#interceptCartChanges();

		// Safety net for full page loads of the cart, where a trigger product may
		// have been removed without a /cart/change.js call this module saw.
		if (document.querySelector('cart-items-component')) this.#syncGifts();
	}

	/**
	 * Intercepts window.fetch to detect calls to /cart/change.js.
	 * After any cart change, syncs gifts to match their trigger products.
	 * This is the only reliable trigger when Recharge handles subscription
	 * removals (the standard cart:update event is suppressed).
	 */
	#interceptCartChanges() {
		this.#originalFetch = window.fetch;
		const self = this;

		window.fetch = function (...args) {
			const result = self.#originalFetch.apply(this, args);
			const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

			if (url.includes('/cart/change.js')) {
				result.then(() => {
					self.#syncGifts();
				}).catch(() => { });
			}
			return result;
		};
	}

	/* ------------------------------------------------------------------------ */
	/*  Event handlers                                                          */
	/* ------------------------------------------------------------------------ */

	/**
	 * Fires on every `cart:update` event (CartAddEvent, CartUpdateEvent, etc.).
	 * @param {Event & { detail?: { data?: { source?: string; productId?: string } } }} event
	 */
	#onCartUpdate = async (event) => {
		const detail = /** @type {Record<string, any>} */ (event.detail?.data || {});

		// Only handle product-form additions (removal is covered by fetch interceptor)
		if (detail.source !== 'product-form-component') return;

		// The form still emits CartAddEvent when the add was rejected (sold out,
		// quantity limit, …) — there is no trigger product to gift against.
		if (detail.didError) return;

		const offer = this.#offers.find(
			(candidate) => candidate.triggerProductId === String(detail.productId),
		);
		if (offer) await this.#addGift(offer);
	};

	/* ------------------------------------------------------------------------ */
	/*  Adding the gift                                                         */
	/* ------------------------------------------------------------------------ */

	/**
	 * Fetches the cart, verifies the trigger product is present (with a
	 * subscription selling plan when the offer requires one), then adds the gift
	 * variant if it isn't in the cart already.
	 * @param {BundleOffer} offer
	 */
	async #addGift(offer) {
		if (this.#adding) return;
		this.#adding = true;

		try {
			const cart = await this.#fetchCart();
			if (!cart) return;

			// Find the trigger line item (may not be present yet if the event fired
			// before the cart was fully updated — bail gracefully)
			const triggerLine = cart.items?.find(
				(/** @type {{ product_id: number }} */ item) =>
					String(item.product_id) === offer.triggerProductId,
			);

			if (!triggerLine) return;

			// Subscription detection via Shopify's standard selling_plan_allocation.
			// Works with Recharge, Shopify Subscriptions, and any other app that
			// uses Shopify selling plans.
			if (offer.requiresSubscription && !triggerLine.selling_plan_allocation) return;

			// One gift per cart.  Any gift line blocks this one, including gifts
			// earned by a different trigger product, so a customer with several
			// qualifying products in the cart still receives exactly one.
			const existingGift = cart.items?.some(
				(/** @type {{ variant_id: number; properties?: Record<string, string> }} */ item) => {
					const properties = item.properties || {};

					// Every gift this module adds carries the trigger property
					if (properties[TRIGGER_PROPERTY_KEY] != null) return true;

					// Gift lines added before `_gift_trigger` existed carry only the
					// visible property, and are only identifiable per offer
					return (
						String(item.variant_id) === offer.giftVariantId &&
						properties[offer.propertyKey] != null
					);
				},
			);
			if (existingGift) return;

			// Build form-encoded body (mirrors what product-form.js sends)
			const formData = new FormData();
			formData.append('id', offer.giftVariantId);
			formData.append('quantity', '1');
			formData.append(`properties[${TRIGGER_PROPERTY_KEY}]`, offer.triggerProductId);
			if (offer.propertyValue) {
				formData.append(`properties[${offer.propertyKey}]`, offer.propertyValue);
			}

			// Include the cart section id so the response carries updated HTML
			const sectionId =
				document.querySelector('cart-items-component')?.dataset.sectionId || '';
			if (sectionId) {
				formData.append('sections', sectionId);
				formData.append('sections_url', window.location.pathname);
			}

			const response = await fetch(
				`${Theme.routes.cart_add_url}`,
				fetchConfig('javascript', { body: formData }),
			);

			const json = await response.json();

			if (json.status) {
				console.error('[bundle-offer] Cart add error:', json.message, json.description);
				return;
			}

			this.#announceCartUpdate(offer.giftVariantId, json.sections);
		} catch (error) {
			console.error('[bundle-offer] Failed to add gift:', error);
		} finally {
			this.#adding = false;
		}
	}

	/* ------------------------------------------------------------------------ */
	/*  Syncing / removing gifts                                                */
	/* ------------------------------------------------------------------------ */

	/**
	 * Removes any gift line whose trigger product is no longer in the cart.
	 * Driven entirely by the `_gift_trigger` property, so it works on the cart
	 * page and anywhere else the offer config isn't rendered.
	 */
	async #syncGifts() {
		// Guard against recursion (#setGiftQuantity also hits /cart/change.js)
		if (this.#syncing) return;
		this.#syncing = true;

		try {
			const cart = await this.#fetchCart();
			if (!cart?.items?.length) return;

			const productIds = new Set(
				cart.items.map((/** @type {{ product_id: number }} */ item) => String(item.product_id)),
			);

			const giftLines = cart.items.filter(
				(/** @type {{ properties?: Record<string, string> }} */ item) =>
					item.properties?.[TRIGGER_PROPERTY_KEY] != null,
			);

			// Keep at most one gift line, at quantity 1.  Changes go by line item
			// key so earlier edits don't shift the lines still to be visited.
			let keptGift = false;

			for (const line of giftLines) {
				const triggerId = String(line.properties[TRIGGER_PROPERTY_KEY]);

				// Drop gifts whose trigger product has left the cart, and any gift
				// beyond the first — including one earned by a different product.
				if (!productIds.has(triggerId) || keptGift) {
					await this.#setGiftQuantity(line.key, 0);
					continue;
				}

				keptGift = true;

				// The customer can raise the quantity of a free line from the cart
				if (line.quantity > 1) await this.#setGiftQuantity(line.key, 1);
			}
		} catch (error) {
			console.error('[bundle-offer] Failed to sync gifts:', error);
		} finally {
			this.#syncing = false;
		}
	}

	/**
	 * Sets the quantity of a cart line by its line item key.  A quantity of 0
	 * removes the line.
	 * @param {string} key
	 * @param {number} quantity
	 */
	async #setGiftQuantity(key, quantity) {
		try {
			const sectionId =
				document.querySelector('cart-items-component')?.dataset.sectionId || '';

			const body = JSON.stringify({
				id: key,
				quantity,
				...(sectionId && {
					sections: sectionId,
					sections_url: window.location.pathname,
				}),
			});

			const response = await fetch(
				`${Theme.routes.cart_change_url}`,
				fetchConfig('json', { body }),
			);

			const json = await response.json();

			this.#announceCartUpdate(key, json.sections);
		} catch (error) {
			console.error('[bundle-offer] Failed to update gift line:', error);
		}
	}

	/* ------------------------------------------------------------------------ */
	/*  Helpers                                                                 */
	/* ------------------------------------------------------------------------ */

	/**
	 * @returns {Promise<Record<string, any> | null>}
	 */
	async #fetchCart() {
		const response = await fetch(`${Theme.routes.cart_url}.js`);
		if (!response.ok) return null;
		return response.json();
	}

	/**
	 * Lets other components know the cart changed.
	 * @param {string} sourceId
	 * @param {Record<string, string> | undefined} sections
	 */
	#announceCartUpdate(sourceId, sections) {
		document.dispatchEvent(
			new CustomEvent(ThemeEvents.cartUpdate, {
				bubbles: true,
				detail: {
					resource: {},
					sourceId,
					data: {
						source: 'bundle-offer',
						sections: sections || {},
					},
				},
			}),
		);
	}
}

/* -------------------------------------------------------------------------- */
/*  Bootstrap                                                                 */
/* -------------------------------------------------------------------------- */

new BundleOfferManager();
